import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { PaymentPresetApi } from '@/api';
import { Plus, Edit, Trash2, X } from 'lucide-react';

export default function PaymentPresets() {
  const { push } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  type StageTrigger = 'order_confirmation' | 'before_dispatch' | 'on_installation' | 'post_installation' | 'net_n_days';
  type Stage = { label: string; percent: number; trigger: StageTrigger; net_days: number | undefined };
  const [formData, setFormData] = useState<{
    name: string;
    currency_applicability: 'INR' | 'USD' | 'EUR' | 'ANY';
    payment_mode: string;
    stages: Stage[];
  }>({
    name: '',
    currency_applicability: 'ANY',
    payment_mode: '',
    stages: [{ label: '', percent: 0, trigger: 'order_confirmation', net_days: undefined }],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const presetsQuery = useQuery({
    queryKey: ['paymentPresets'],
    queryFn: () => PaymentPresetApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => PaymentPresetApi.create(data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Preset created' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['paymentPresets'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to create preset', body: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => PaymentPresetApi.update(editId!, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Preset updated' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['paymentPresets'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update preset', body: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => PaymentPresetApi.remove(id),
    onSuccess: () => {
      push({ tone: 'success', title: 'Preset deleted' });
      queryClient.invalidateQueries({ queryKey: ['paymentPresets'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to delete preset', body: err.message });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      currency_applicability: 'ANY',
      payment_mode: '',
      stages: [{ label: '', percent: 0, trigger: 'order_confirmation' as StageTrigger, net_days: undefined }],
    });
    setEditId(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.payment_mode) newErrors.payment_mode = 'Payment mode is required';
    if (formData.stages.length === 0) newErrors.stages = 'At least one stage required';

    let totalPercent = 0;
    formData.stages.forEach((s, i) => {
      if (!s.label) newErrors[`stage_${i}_label`] = 'Label required';
      if (s.percent <= 0 || s.percent > 100) newErrors[`stage_${i}_percent`] = '1-100%';
      totalPercent += s.percent;
    });

    if (totalPercent !== 100) newErrors.stagesTotal = `Total must be 100% (current: ${totalPercent}%)`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (preset?: any) => {
    if (preset) {
      setFormData({
        name: preset.name,
        currency_applicability: preset.currency_applicability,
        payment_mode: preset.payment_mode,
        stages: preset.stages_json,
      });
      setEditId(preset.id);
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const data = {
      name: formData.name,
      currency_applicability: formData.currency_applicability,
      payment_mode: formData.payment_mode,
      stages_json: formData.stages.map((s) => ({
        label: s.label,
        percent: s.percent,
        trigger: s.trigger,
        ...(s.trigger === 'net_n_days' && { net_days: s.net_days }),
      })),
    };

    if (editId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const totalPercent = formData.stages.reduce((sum, s) => sum + (s.percent || 0), 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Payment Presets"
        subtitle={isAdmin ? 'Define standard payment terms' : 'View standard payment terms'}
        actions={
          isAdmin ? (
            <button
              className="btn btn-primary gap-2"
              onClick={() => handleOpenModal()}
            >
              <Plus size={16} /> Add Preset
            </button>
          ) : undefined
        }
      />

      {/* Table */}
      {presetsQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : !presetsQuery.data || presetsQuery.data.length === 0 ? (
        <EmptyState
          title="No payment presets"
          body={isAdmin ? 'Create your first payment preset to standardize terms.' : 'No payment presets have been added yet.'}
          action={
            isAdmin ? (
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal()}
              >
                Add Preset
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Currency</th>
                  <th>Stages</th>
                  <th>Payment Mode</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {presetsQuery.data.map((p: any) => (
                  <tr key={p.id}>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-sm">{p.currency_applicability}</td>
                    <td className="text-sm text-muted">{p.stages_json?.length || 0} stages</td>
                    <td className="text-sm text-muted">{p.payment_mode}</td>
                    {isAdmin && (
                      <td className="flex gap-1">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleOpenModal(p)}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteMutation.mutate(p.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={editId ? 'Edit Payment Preset' : 'Add Payment Preset'}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-4 max-h-96 overflow-y-auto">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. 30-30-40"
            />
            {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="label">Currency Applicability</label>
            <select
              className="input mt-1"
              value={formData.currency_applicability}
              onChange={(e) => setFormData({ ...formData, currency_applicability: e.target.value as any })}
            >
              <option value="ANY">Any Currency</option>
              <option value="INR">INR Only</option>
              <option value="USD">USD Only</option>
              <option value="EUR">EUR Only</option>
            </select>
          </div>

          <div>
            <label className="label">Payment Mode *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.payment_mode ? 'input-error' : ''}`}
              value={formData.payment_mode}
              onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
              placeholder="e.g. Bank Transfer, Cheque"
            />
            {errors.payment_mode && <div className="text-xs text-rdx mt-1">{errors.payment_mode}</div>}
          </div>

          <div className="border-t border-line pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Payment Stages</h4>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() =>
                  setFormData({
                    ...formData,
                    stages: [
                      ...formData.stages,
                      { label: '', percent: 0, trigger: 'order_confirmation' as StageTrigger, net_days: undefined },
                    ],
                  })
                }
                disabled={formData.stages.length >= 4}
              >
                + Add Stage
              </button>
            </div>

            {errors.stagesTotal && (
              <div className={`text-xs p-2 rounded mb-2 ${totalPercent === 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-rdx-50 text-rdx'}`}>
                Total: {totalPercent}%
              </div>
            )}

            <div className="space-y-3">
              {formData.stages.map((stage, idx) => (
                <div key={idx} className="p-2 border border-line rounded-lg space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="label text-xs">Stage Label</label>
                      <input
                        type="text"
                        className={`input mt-1 text-sm ${errors[`stage_${idx}_label`] ? 'input-error' : ''}`}
                        value={stage.label}
                        onChange={(e) => {
                          const newStages = [...formData.stages];
                          newStages[idx].label = e.target.value;
                          setFormData({ ...formData, stages: newStages });
                        }}
                        placeholder="e.g. Advance"
                      />
                    </div>
                    <div className="w-20">
                      <label className="label text-xs">%</label>
                      <input
                        type="number"
                        className={`input mt-1 text-sm ${errors[`stage_${idx}_percent`] ? 'input-error' : ''}`}
                        value={stage.percent}
                        onChange={(e) => {
                          const newStages = [...formData.stages];
                          newStages[idx].percent = Number(e.target.value);
                          setFormData({ ...formData, stages: newStages });
                        }}
                        min={0}
                        max={100}
                      />
                    </div>
                    {formData.stages.length > 1 && (
                      <button
                        className="btn btn-sm btn-danger self-end"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            stages: formData.stages.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="label text-xs">Trigger</label>
                    <select
                      className="input mt-1 text-sm"
                      value={stage.trigger}
                      onChange={(e) => {
                        const newStages = [...formData.stages];
                        newStages[idx].trigger = e.target.value as any;
                        setFormData({ ...formData, stages: newStages });
                      }}
                    >
                      <option value="order_confirmation">Order Confirmation</option>
                      <option value="before_dispatch">Before Dispatch</option>
                      <option value="on_installation">On Installation</option>
                      <option value="post_installation">Post Installation</option>
                      <option value="net_n_days">Net N Days</option>
                    </select>
                  </div>

                  {stage.trigger === 'net_n_days' && (
                    <div>
                      <label className="label text-xs">Days</label>
                      <input
                        type="number"
                        className="input mt-1 text-sm"
                        value={stage.net_days || 0}
                        onChange={(e) => {
                          const newStages = [...formData.stages];
                          newStages[idx].net_days = Number(e.target.value);
                          setFormData({ ...formData, stages: newStages });
                        }}
                        min={1}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {errors.stages && <div className="text-xs text-rdx mt-2">{errors.stages}</div>}
          </div>
        </div>
      </Modal>
    </div>
  );
}
